import { useCallback, useEffect, useRef, useState } from "react";
import {
  DRAWN_GRID_M,
  DRAWN_MIN_SIDE_M,
  EMPTY_DRAWN_PLAN,
  snapDrawnRoom,
  snapToGrid,
  validateDrawnPlan,
  type DrawnPlan,
  type DrawnRoom,
  type PlanPoint,
} from "@sts/geometry";
import type { ApiClient } from "../api/client.js";
import { ApiError } from "../api/client.js";
import type { SavedSurveyDrawing, SurveyDrawingState } from "../api/types.js";

/**
 * The hand-drawn floor plan, held for the Survey page (migration 0047).
 *
 * WHAT IT HOLDS is the drawing itself — the rectangles the surveyor placed —
 * and nothing else. The capture the plan, the objects and the tables are drawn
 * from is derived from it by `drawnPlanCapture`, the same pure function the
 * API uses, so what is on screen and what the heat loss was calculated from
 * cannot be two different shapes.
 *
 * OPTIMISTIC, AND SERIALISED. An edit is shown at once and saved behind it,
 * one save at a time (a chained promise, as `usePlanEdits` does): dragging a
 * room out, typing a wall length and renaming a room in quick succession send
 * three saves in that order, never two at once and never out of order. Each
 * save carries the version the last one returned, so a colleague saving in
 * another tab is caught (409) rather than silently overwritten; the answer is
 * shown and the drawing re-read.
 *
 * REFUSALS ARE SHOWN, NOT SWALLOWED. The API refuses a room with no ceiling
 * height, and so does this before sending — the same rule
 * (`validateDrawnPlan`), so the surveyor is told in the same words either way
 * and a bad drawing never reaches the wire.
 */

export interface DrawnPlanSource {
  /** `null` when this API has no drawing route (it predates 0047). */
  read(): Promise<SurveyDrawingState | null>;
  save(plan: DrawnPlan, baseVersion?: number): Promise<SavedSurveyDrawing>;
  remove(): Promise<void>;
}

export function drawnPlanFromApi(client: ApiClient, surveyId: string): DrawnPlanSource {
  return {
    // `async`, and the method checked before it is called, so a client built
    // before this existed leaves the page without a drawing rather than
    // failing it — the lesson the tape measurements section learned.
    read: async () =>
      typeof client.surveyDrawing === "function" ? await client.surveyDrawing(surveyId) : null,
    save: (plan, baseVersion) => client.saveSurveyDrawing(surveyId, plan, baseVersion),
    remove: () => client.deleteSurveyDrawing(surveyId),
  };
}

export type DrawnPlanStatus = "loading" | "ready" | "unsupported" | "failed";

export interface DrawnPlanState {
  readonly status: DrawnPlanStatus;
  /** What is drawn. The empty plan until the surveyor draws something. */
  readonly plan: DrawnPlan;
  /** True once a drawing has been saved for this survey. */
  readonly exists: boolean;
  /** False when the survey has a scan: its plan comes from that instead. */
  readonly canDraw: boolean;
  readonly saving: boolean;
  /** A refusal, in the API's own words. Null when there is nothing to say. */
  readonly error: string | null;
  /** What became of the heat loss after the last save, when it is worth saying. */
  readonly note: string | null;
  /** Replaces the whole drawing: shown at once, saved behind. */
  readonly update: (next: DrawnPlan) => void;
  readonly reload: () => void;
  readonly dismissError: () => void;
  readonly dismissNote: () => void;
}

/**
 * A room id nothing else in the drawing uses.
 *
 * Ids are permanent — the heat-loss room, its walls and anything placed on
 * them are all named by this — so a repeat would join two rooms into one.
 * `taken` lets a caller minting several at once (a storey copied up) pass the
 * ids it has already handed out but not yet added to the plan.
 */
export function nextRoomId(plan: DrawnPlan, taken: ReadonlySet<string> = new Set()): string {
  const used = new Set([...plan.rooms.map((r) => r.id), ...taken]);
  let n = used.size + 1;
  while (used.has(`room-${n}`)) n += 1;
  return `room-${n}`;
}

/**
 * A room from two corners dragged on the plan, landed on the grid and pulled
 * onto its neighbours.
 *
 * The corners are rounded to the grid FIRST and the room built from those, so
 * what the surveyor let go of is a whole number of centimetres rather than
 * 4.187 m; then `snapDrawnRoom` translates the whole room onto whatever it is
 * next to, which cannot change either of those sizes.
 */
export function roomFromCorners(
  plan: DrawnPlan,
  storey: number,
  a: PlanPoint,
  b: PlanPoint,
  name: string,
  ceilingHeightM: number,
): DrawnRoom | undefined {
  const x0 = snapToGrid(Math.min(a.x, b.x), DRAWN_GRID_M);
  const x1 = snapToGrid(Math.max(a.x, b.x), DRAWN_GRID_M);
  const y0 = snapToGrid(Math.min(a.y, b.y), DRAWN_GRID_M);
  const y1 = snapToGrid(Math.max(a.y, b.y), DRAWN_GRID_M);
  if (x1 - x0 < DRAWN_MIN_SIDE_M || y1 - y0 < DRAWN_MIN_SIDE_M) return undefined;
  // To the millimetre: two grid positions subtracted in binary floating point
  // come out 0.9000000000000004, and a room's size is a figure a person reads.
  const toMm = (metres: number): number => Math.round(metres * 1000) / 1000;
  const room: DrawnRoom = {
    id: nextRoomId(plan),
    name,
    story: storey,
    centre: { x: toMm((x0 + x1) / 2), y: toMm((y0 + y1) / 2) },
    widthM: toMm(x1 - x0),
    depthM: toMm(y1 - y0),
    rotationDeg: 0,
    ceilingHeightM,
  };
  return snapDrawnRoom(room, plan.rooms).room;
}

export function useDrawnPlan(
  source: DrawnPlanSource | undefined,
  surveyId: string,
): DrawnPlanState {
  const [status, setStatus] = useState<DrawnPlanStatus>(source ? "loading" : "unsupported");
  const [plan, setPlan] = useState<DrawnPlan>(EMPTY_DRAWN_PLAN);
  const [exists, setExists] = useState(false);
  const [canDraw, setCanDraw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);

  /** The version the server last confirmed; 0 when nothing is stored. */
  const version = useRef(0);
  /** One save at a time, in the order the edits were made. */
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (!source) {
      setStatus("unsupported");
      return;
    }
    let current = true;
    setStatus("loading");
    source
      .read()
      .then((state) => {
        if (!current || !alive.current) return;
        if (state === null) {
          setStatus("unsupported");
          return;
        }
        version.current = state.drawing?.version ?? 0;
        setPlan(state.drawing?.plan ?? EMPTY_DRAWN_PLAN);
        setExists(state.drawing !== null);
        setCanDraw(state.canDraw);
        setStatus("ready");
      })
      .catch(() => {
        if (!current || !alive.current) return;
        setStatus("failed");
      });
    return () => {
      current = false;
    };
  }, [source, surveyId, reloads]);

  const update = useCallback(
    (next: DrawnPlan) => {
      if (!source) return;
      const issues = validateDrawnPlan(next);
      if (issues.length > 0) {
        setError(issues.join(" "));
        return;
      }
      setPlan(next);
      setError(null);
      setSaving(true);
      queue.current = queue.current
        .then(() => source.save(next, version.current))
        .then((saved) => {
          if (!alive.current) return;
          version.current = saved.drawing.version;
          setExists(true);
          setNote(heatLossNote(saved));
        })
        .catch((err: unknown) => {
          if (!alive.current) return;
          setError(refusalMessage(err));
          // Whatever the drawing now is, this editor no longer knows: re-read
          // it rather than saving over a colleague's work on the next edit.
          setReloads((n) => n + 1);
        })
        .finally(() => {
          if (alive.current) setSaving(false);
        });
    },
    [source],
  );

  return {
    status,
    plan,
    exists,
    canDraw,
    saving,
    error,
    note,
    update,
    reload: () => setReloads((n) => n + 1),
    dismissError: () => setError(null),
    dismissNote: () => setNote(null),
  };
}

/** The API's own words for a refusal; anything else is said plainly. */
function refusalMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "The drawing could not be saved. Check your connection and try again.";
}

/**
 * What to say about the heat loss after a save — only when there is something
 * to say. A drawing that simply became the heat loss says nothing: the
 * figures on the page are about to show it.
 */
function heatLossNote(saved: SavedSurveyDrawing): string | null {
  const heatLoss = saved.heatLoss;
  if (heatLoss.status === "kept") return heatLoss.note;
  if (heatLoss.status === "recalculated" && heatLoss.note) return heatLoss.note;
  return null;
}
